import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { SHIPMENT_STATUS_OPTIONS_QUERY } from '../../app/graphql/jobFilterQueries';
import { AIRPORTS_QUERY } from '../../app/graphql/jobQueries';
import { extractGraphqlEntity } from '../../common/utils/graphqlUtils';
import { useSortToggle } from '../../common/utils/sortUtils';
import IconButton from '../../components/buttons/IconButton';
import { FiltersAndSortContext } from '../../components/data/FiltersAndSortProvider';
import { useAccountOptions } from '../../components/domainSpecific/accountElements';
import { isFormValuePresent } from '../../components/forms/formHelpers';
import { useFormContext } from '../../components/forms/forms';
import { mapSelectOptions } from '../../components/forms/selectUtils';
import { Popup } from '../../components/nav/navElements';
import { useDateRangePresets } from '../../config/dateRangeConstants';
import { useMonitorSearchFiltersStyles } from './MonitorSearchFilters.styles';

export function FilterPopup({
  className,
  rootClassName,
  isPresent,
  customIcon,
  ...rest
}) {
  const { styles } = useMonitorSearchFiltersStyles();
  return (
    <div
      className={classNames(
        'MonitorSearchFilters-PopupContainer',
        styles.popupContainer
      )}
    >
      <Popup
        className={classNames(
          'MonitorSearchFilters-PopupLabel',
          styles.popupLabel,
          className
        )}
        rootClassName={classNames(
          'MonitorSearchFilters-Popup',
          styles.popup,
          rootClassName
        )}
        align={{
          offset: [0, -8],
        }}
        trigger="click"
        getPopupContainer={triggerNode => triggerNode.parentNode}
        customClosedIcon={customIcon}
        placement="bottom"
        {...rest}
      />
    </div>
  );
}

export function DateRangeFilterPreviewText({ DateFormat, value }) {
  const { labelIdsByKey } = useDateRangePresets();
  const labelId = labelIdsByKey[value?.namedRange];
  return labelId ? (
    <FormattedMessage id={labelId} />
  ) : (
    <>
      <DateFormat value={value?.from} />
      <span className="margin-hor-xs">
        <FormattedMessage id="filters.labels.rangeJoin" />
      </span>
      <DateFormat value={value?.to} />
    </>
  );
}

export function useSimpleAccountOptions() {
  const { options } = useAccountOptions();
  return mapSelectOptions(options, opt => ({
    ...opt,
    value: opt.value.number,
    account: opt.value,
  }));
}

export function useStatusOptions() {
  const { data } = useQuery(SHIPMENT_STATUS_OPTIONS_QUERY);
  return data
    ? extractGraphqlEntity(data).map(({ code, text }) => ({
        value: code,
        label: text,
        title: text,
      }))
    : [];
}

export function useAirportOptions() {
  const { data } = useQuery(AIRPORTS_QUERY);
  return useMemo(
    () =>
      (data?.getAirports || []).map(a => ({
        value: a.airport,
        label: `${a.airport} — ${a.airportName}`,
      })),
    [data]
  );
}

function SortIcon({ name }) {
  const {
    sort: { values: sort, setValues: setSort },
  } = useContext(FiltersAndSortContext);
  const { styles } = useMonitorSearchFiltersStyles();

  const { active, icon, onClick } = useSortToggle({ name, sort, setSort });

  return (
    <IconButton
      className={classNames('MonitorSearchFilters-SortIcon', styles.sortIcon, {
        active,
      })}
      icon={icon}
      onClick={onClick}
    />
  );
}

export function MonitorFilter({ definition }) {
  const { values } = useFormContext();

  const {
    name,
    filterComponent: FC,
    isFilterPresent = isFormValuePresent,
    isSortable,
  } = definition;
  const isPresent = isFilterPresent(get(values, name));
  return (
    <>
      <FC name={name} isPresent={isPresent} />
      {isSortable && <SortIcon name={name} />}
    </>
  );
}
